import { Component, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  ActiveStatusEnum,
  Addon,
  BarcodePrintStatusEnum,
  ItemDetail,
  ItemDetailStatusEnum,
  JoDetail,
  ProductTypeAlt,
} from "app/model/entities/entity-model";
import { AddonUtilsService } from "app/shared/addon-utils.service";
import { BarcodeService } from "app/shared/barcode.service";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { PullMatchFns, ReplacementMap } from "app/shared/pull-match-fns";
import { ScannerInputComponent } from "app/shared/scanner-input.component";
import { UtilFns } from "app/shared/util-fns";
import _ from "lodash";

interface ItemDetailMatch {
  itemDetail: ItemDetail;
  fitness: string;
  fitnessRank: number;
}

@Component({
  selector: "app-notepad-pull-apply",
  templateUrl: "./notepad-pull-apply.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class NotepadPullApplyComponent extends DomainBaseComponent implements OnInit {
  @ViewChild(ScannerInputComponent) scannerInput!: ScannerInputComponent<ItemDetail>;

  joPullBatchId!: number;
  joDetailId!: number;
  joDetail!: JoDetail;
  accountAddons: Addon[] = [];

  shouldPrintBarcodes = true;
  isAccountOwnedPull = false;
  hasAccountOwnedMatches = false;
  allowNonAccountOwned = false;

  matches: ItemDetailMatch[] = [];
  replacementMap: ReplacementMap = new Map();
  errorMessage = "";

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private addonUtilsService: AddonUtilsService,
    private barcodeService: BarcodeService
  ) {
    super(domainService);
  }

  override async ngOnInit() {
    super.ngOnInit();

    this.joPullBatchId = +this.route.snapshot.params["joPullBatchId"];
    this.joDetailId = +this.route.snapshot.params["joDetailId"];
    this.shouldPrintBarcodes = this.route.snapshot.params["shouldPrintBarcodes"] === "true";

    this.joDetail = (await this.dbQueryService.getJoDetailById(this.joDetailId)) as any;

    const joh = this.joDetail?.joHeader;
    if (joh?.allowInventory && joh?.inventoryProgramName) {
      const programProductTypeIds = await this.dbQueryService.getAccountOwnedProductTypeIds(
        joh.inventoryProgramName
      );
      this.isAccountOwnedPull = programProductTypeIds.includes(this.joDetail.product?.productTypeId);
    }

    this.accountAddons = await this.dbQueryService.getAddons(this.joDetail.joHeader.accountId);
    // Before the scanner is ready: a scan of a mapped old item must not read as a style mismatch.
    this.replacementMap = await this.dbQueryService.getReplacementMapIntoStyle(this.joDetail.product.productTypeId);

    this.isPageReady = true;

    // Background match load — non-blocking so the scanner is usable immediately
    void this.loadMatches();

    await this.UtilFns.wait(1);
    this.scannerInput?.focus();
  }

  goBack() {
    this.router.navigate(["/notepad/pull", this.joPullBatchId], {
      queryParams: {
        shouldPrintBarcodes: this.shouldPrintBarcodes,
        from: this.joDetailId,
      },
    });
  }

  // -------- match list (shown below scanner) --------

  private async loadMatches() {
    const jod = this.joDetail;
    const productId = jod.productId;
    const productTypeId = jod.product.productType.id;

    let r = await this.dbQueryService.getAltItemDetailsForStyle(productTypeId);
    r = r.filter(
      (itd) =>
        itd.accountId == null ||
        (jod.joHeader.allowInventory && jod.joHeader.accountId == itd.accountId)
    );
    r = r.filter((itd) => PullMatchFns.mappedVerdict(this.replacementMap, itd.productId, productId) !== "mappedElsewhere");

    const ranked: ItemDetailMatch[] = r.map((itd) => {
      let fitnessRank = 3;
      let fitness = "Alt. product type match";
      if (itd.productId === productId) {
        fitnessRank = 0;
        fitness = "Exact match";
      } else if (PullMatchFns.mappedVerdict(this.replacementMap, itd.productId, productId) === "mapped") {
        fitnessRank = 0;
        fitness = "Old style, mapped";
      } else if (itd.product.productTypeId === productTypeId) {
        fitnessRank = 1;
        fitness = "Product type match";
      } else {
        fitnessRank = 2;
        fitness = "Alt. product match";
      }
      return { itemDetail: itd, fitness, fitnessRank };
    });

    this.hasAccountOwnedMatches =
      jod.joHeader.allowInventory && r.some((itd) => itd.accountId != null);

    this.matches = _.orderBy(
      ranked,
      [(m) => (m.itemDetail.accountId != null ? 0 : 1), (m) => m.fitnessRank],
      ["asc", "asc"]
    );
  }

  fitnessBadgeClass(rank: number): string {
    if (rank === 0) return "np-badge-complete";
    if (rank === 1) return "np-badge-await";
    return "np-badge-pending";
  }

  // -------- scanner --------

  async scannerSearch(value: string): Promise<ItemDetail | null> {
    return (await this.dbQueryService.getItemDetailById(value)) as any;
  }

  async onScanResult(itd: ItemDetail) {
    await this.processItemDetail(itd);
  }

  async onScanError(_message: string) {
    await this.UtilFns.wait(1);
    this.scannerInput?.focus();
  }

  async onTapMatch(match: ItemDetailMatch) {
    if (!this.user.isPullAdmin) return;
    await this.processItemDetail(match.itemDetail);
  }

  // -------- validate + commit (ported from jo-pull-apply) --------

  @wrapBusy
  async processItemDetail(itd: ItemDetail) {
    if (this.joDetail.itemDetails.length >= this.joDetail.orderQty) {
      return;
    }

    this.errorMessage = "";

    if (itd.joDetailId != null) {
      this.errorMessage = `This inventory item is reserved for JO Detail: ${itd.joDetailId}`;
      await this.refocusScanner();
      return;
    }

    if (this.hasAccountOwnedMatches && !this.allowNonAccountOwned && itd.accountId == null) {
      this.errorMessage =
        "Non-account-owned items are not allowed. Toggle 'Allow non-Account-Owned' to override.";
      await this.refocusScanner();
      return;
    }

    const fcs = itd.product.productFeatureChoices.map((x) => x.featureChoice);
    for (const fc of fcs) {
      if (fc.activeStatusId == ActiveStatusEnum.Inactive) {
        this.errorMessage = `Feature Choice '${fc.choiceValue}' on '${fc.feature.name}' is inactive.`;
        await this.refocusScanner();
        return;
      }
      if (fc.feature.activeStatusId == ActiveStatusEnum.Inactive) {
        this.errorMessage = `Feature '${fc.feature.name}' is inactive.`;
        await this.refocusScanner();
        return;
      }
    }

    const jod = this.joDetail;
    const mapped = PullMatchFns.mappedVerdict(this.replacementMap, itd.productId, jod.productId);
    if (mapped === "mappedElsewhere") {
      this.errorMessage = PullMatchFns.mappedElsewhereMessage(jod.product.productType.style);
      await this.refocusScanner();
      return;
    }
    // An old item mapped to this line's product is an exact match: no alternate style is registered.
    const isStyleSizeMismatch = itd.productId !== jod.productId && mapped !== "mapped";

    if (isStyleSizeMismatch) {
      let alternativeAlreadyExists = false;
      if (itd.product.productTypeId !== jod.product.productTypeId) {
        alternativeAlreadyExists = jod.product.productType.productTypeAlts.some(
          (ss) =>
            ss.productTypeId === jod.product.productTypeId &&
            ss.altProductTypeId === itd.product.productTypeId
        );
      } else {
        alternativeAlreadyExists = true;
      }

      if (!alternativeAlreadyExists) {
        const ok = await this.dialogService.yesNoDialog(
          "Style/Size mismatch",
          `Scanned item is style ${itd.product?.productType?.style} but JO requests ${jod.product?.productType?.style}. Register as a valid alternative and continue?`
        );
        if (!ok) {
          await this.refocusScanner();
          return;
        }
        const altKey = [jod.product.productTypeId, itd.product.productTypeId];
        if (this.uow.getEntityByKey(ProductTypeAlt, altKey) == null) {
          this.uow.createEntity(ProductTypeAlt, {
            productTypeId: jod.product.productTypeId,
            altProductTypeId: itd.product.productTypeId,
          });
        }
      }
    }

    await this.commitItemDetail(itd);
  }

  private async commitItemDetail(itd: ItemDetail) {
    const jod = this.joDetail;
    if (jod.itemDetails.length >= jod.orderQty) return;

    itd.joDetailId = jod.id;
    itd.barcodePrintStatusId = BarcodePrintStatusEnum.NotYetPrinted;
    itd.lastScanTs = new Date(Date.now());

    const itdAddons = this.addonUtilsService.createItemDetailAddonsFromJoDetail(
      jod,
      itd,
      this.accountAddons
    );
    if (itd.itemDetailAddons.length > 0) {
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessPendingAddon;
    } else {
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessPendingShipPrep;
    }
    const addonBins = await this.addonUtilsService.findOrCreateAddonBins([itd]);
    await this.addonUtilsService.checkAndRemoveEmptyBins(addonBins.map((x) => x.id));

    if (itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingAddon) {
      const unassigned = itd.itemDetailAddons.filter(ida => ida.addonBinId == null);
      if (unassigned.length > 0) {
        throw new Error(`ItemDetail ${itd.id}: ${unassigned.length} of ${itd.itemDetailAddons.length} addon(s) missing addonBinId after findOrCreateAddonBins; aborting save.`);
      }
    }

    await this.dbSaveService.saveChanges();

    if (this.shouldPrintBarcodes) {
      const changes = await this.barcodeService.printItemDetailBarcodesAndNewAddonBinLabels([itd]);
      if (changes.length > 0) {
        await this.dbSaveService.saveSelectedChanges(changes);
      }
    }

    this.toastr.success("Item reserved");

    // remove the just-applied item from the match list
    this.matches = this.matches.filter((m) => m.itemDetail.id !== itd.id);

    if (jod.itemDetails.length >= jod.orderQty) {
      this.goBack();
      return;
    }

    await this.refocusScanner();
  }

  private async refocusScanner() {
    await UtilFns.wait(1);
    this.scannerInput?.focus();
  }

  // -------- template helpers --------

  get qtyApplied(): number {
    return this.joDetail?.itemDetails?.length ?? 0;
  }

  get qtyRemaining(): number {
    if (!this.joDetail) return 0;
    return Math.max(0, this.joDetail.orderQty - this.qtyApplied);
  }
}
